export type StepStatus = "pending" | "in_progress" | "completed" | "failed" | "blocked";

export interface StepState {
  step: string;
  status: StepStatus;
  started_at: string | null;
  completed_at: string | null;
  completed_by: string | null;
  error: string | null;
  data: Record<string, any> | null;
}

export interface Pipeline {
  current_step: string;
  steps: StepState[];
  started_at: string;
  updated_at: string;
  completed_at: string | null;
}

// Placeholder Compass returns instead of credential values (see Compass CompanyService.masked_credentials_value).
export const MASKED_CREDENTIAL = "***REDACTED***";

export interface IntegrationConfig {
  status: "inactive" | "onboarding" | "active";
  validation?: Pipeline;
  [key: string]: any;
}

export interface ColorSet {
  background?: string;
  text?: string;
  link?: string;
  links_underlined?: boolean;
}

export interface LinkFields {
  website_url?: string;
  contact_page?: string;
  buy_page?: string;
  rent_page?: string;
  sell_page?: string;
  let_page?: string;
  valuation_page?: string;
  blog_page?: string;
  team_page?: string;
  about_page?: string;
  faq_page?: string;
  jobs_page?: string;
  privacy_policy_page?: string;
  conditions_page?: string;
  cookie_policy_page?: string;
}

export interface SocialFields {
  facebook_url?: string;
  instagram_url?: string;
  linkedin_url?: string;
  youtube_url?: string;
}

export type SocialIconStyle =
  | "rounded-square"
  | "rounded-square-outlined"
  | "circle"
  | "circle-outlined"
  | "square"
  | "square-outlined"
  | "minimal"
  | "minimal-outlined";

export const socialIconStyles: { id: SocialIconStyle; label: string; row: 0 | 1; description: string }[] = [
  { id: "rounded-square", label: "Rounded Square", row: 0, description: "Filled" },
  { id: "circle", label: "Circle", row: 0, description: "Filled" },
  { id: "square", label: "Square", row: 0, description: "Filled" },
  { id: "minimal", label: "Minimal", row: 0, description: "Icon only" },
  { id: "rounded-square-outlined", label: "Rounded Square", row: 1, description: "Outlined" },
  { id: "circle-outlined", label: "Circle", row: 1, description: "Outlined" },
  { id: "square-outlined", label: "Square", row: 1, description: "Outlined" },
  { id: "minimal-outlined", label: "Minimal", row: 1, description: "Outlined icon" },
];

export interface ConfigDesignTemp {
  communication_style?: "formal" | "informal";
  images?: {
    logo?: string;
    team?: string;
  };
  colors?: {
    primary?: ColorSet;
    secondary?: ColorSet;
    accent?: ColorSet;
  };
  typography?: {
    font_family?: string;
    heading_font?: string;
    font_size_body?: number;
    font_size_heading?: number;
    line_height?: number;
  };
  links?: {
    nl?: LinkFields;
    fr?: LinkFields;
    en?: LinkFields;
  };
  socials?: SocialFields & {
    icon_style?: SocialIconStyle;
  };
}

export interface CompanyType {
  id?: string;
  name: string;
  info?: CompanyInfo;
  config_design_temp?: ConfigDesignTemp;
  services?: {
    [key: string]: any;
  };
  integrations?: {
    [key: string]: IntegrationConfig;
  };
  status: "active" | "inactive" | "onboarding";
  legacy_data?: any;
  created_at?: string;
  updated_at?: string;
  merge_config?: MergeConfig;
}

export type LanguageCode = "nl" | "fr" | "en";

// Ordered from least to most capable: "default" is the fallback language and is
// implicitly also "live" and "translatable"; "live" is implicitly also "translatable".
export const languageStatuses = ["translatable", "live", "default"] as const;

export type LanguageStatus = (typeof languageStatuses)[number];

export interface CompanyInfo {
  name: string;
  languages?: Partial<Record<LanguageCode, LanguageStatus | null>>;
  email?: string;
  website_url?: string;
  comments?: string;
}

// The DEFAULT language is the fallback language - at most one language should carry it.
export function getFallbackLanguage(languages?: CompanyInfo["languages"]): LanguageCode | undefined {
  if (!languages) return undefined;
  return (Object.keys(languages) as LanguageCode[]).find((lang) => languages[lang] === "default");
}

// DEFAULT implies LIVE.
export function getLiveLanguages(languages?: CompanyInfo["languages"]): LanguageCode[] {
  if (!languages) return [];
  return (Object.keys(languages) as LanguageCode[]).filter(
    (lang) => languages[lang] === "default" || languages[lang] === "live",
  );
}

// DEFAULT and LIVE both imply TRANSLATABLE.
export function getTranslatableLanguages(languages?: CompanyInfo["languages"]): LanguageCode[] {
  if (!languages) return [];
  return (Object.keys(languages) as LanguageCode[]).filter((lang) => !!languages[lang]);
}

export type CompanyLanguages = NonNullable<CompanyInfo["languages"]>;

const languageStatusRank: Record<LanguageStatus, number> = {
  translatable: 1,
  live: 2,
  default: 3,
};

// Whether a stored status is at least as capable as `minimum` - "default" counts
// as live and translatable, "live" counts as translatable.
export function hasLanguageStatus(
  status: LanguageStatus | null | undefined,
  minimum: LanguageStatus,
): boolean {
  if (!status) return false;
  return languageStatusRank[status] >= languageStatusRank[minimum];
}

// Exactly one language carries DEFAULT - the previous one stays enabled as LIVE.
export function setDefaultLanguage(
  languages: CompanyLanguages | undefined,
  langId: LanguageCode,
): CompanyLanguages {
  const next: CompanyLanguages = { ...languages };
  for (const otherLang of Object.keys(next) as LanguageCode[]) {
    if (next[otherLang] === "default") next[otherLang] = "live";
  }
  next[langId] = "default";
  return next;
}

// A null status removes the language from the map, which is how "not used" is stored.
export function setLanguageStatus(
  languages: CompanyLanguages | undefined,
  langId: LanguageCode,
  status: LanguageStatus | null,
): CompanyLanguages {
  const next: CompanyLanguages = { ...languages };
  if (status === null) delete next[langId];
  else next[langId] = status;
  return next;
}

export interface MergeConfig {
  match_fields?: string[];
  source_priority?: string[];
  field_priority_overrides?: {
    [key: string]: string[];
  };
}

export type CompanyTypeFields = keyof CompanyType;

export interface CompanyFilter {
  status: string;
}

export interface MailTestData {
  sendLive: false;
  sendDraft: true;
  sendActive: false;
  // The editor's raw text, tagged with the template+dataset it was typed against. Edits are
  // dropped rather than carried over when that tag no longer matches.
  editedFields: { key: string; text: string } | null;
  // Delivery settings that outlive a template switch. See STICKY_TEST_FIELDS.
  stickyFields: Record<string, unknown>;
  datasetName: string | null;
  languages: string[];
}

export const companyStatuses = [
  { id: "active", name: "Active" },
  { id: "inactive", name: "Inactive" },
  { id: "onboarding", name: "Onboarding" },
];

export const companyIntegrations = [
  { id: "whise", name: "Whise", hasPipeline: false, autoActivateCanOverride: false, autoActivateDefault: false },
  {
    id: "pipedrive",
    name: "Pipedrive",
    hasPipeline: false,
    autoActivateCanOverride: false,
    autoActivateDefault: false,
  },
  { id: "zabun", name: "Zabun", hasPipeline: false, autoActivateCanOverride: false, autoActivateDefault: false },
  { id: "zabun360", name: "Zabun360", hasPipeline: false, autoActivateCanOverride: false, autoActivateDefault: false },
  { id: "omnicasa", name: "Omnicasa", hasPipeline: false, autoActivateCanOverride: false, autoActivateDefault: false },
  {
    id: "dotdigital",
    name: "Dotdigital",
    hasPipeline: false,
    autoActivateCanOverride: false,
    autoActivateDefault: false,
  },
  { id: "sendgrid", name: "SendGrid", hasPipeline: true, autoActivateCanOverride: true, autoActivateDefault: true },
  { id: "hubspot", name: "Hubspot", hasPipeline: false, autoActivateCanOverride: false, autoActivateDefault: false },
  { id: "skarabee", name: "Skarabee", hasPipeline: false, autoActivateCanOverride: false, autoActivateDefault: false },
  {
    id: "salesforce",
    name: "Salesforce",
    hasPipeline: false,
    autoActivateCanOverride: false,
    autoActivateDefault: false,
  },
];

// Integrations that act as the company's CRM (shown as a pill next to the company name).
export const crmIntegrations = ["whise", "zabun", "zabun360", "omnicasa"];

// Entity types that can be polled per integration. Only integrations listed
// here support manual polling; the values mirror the backend's supported
// `entity_types` for the poll endpoint.
export const integrationPollableEntityTypes: Record<string, string[]> = {
  whise: ["contacts", "representatives", "estates", "tasks"],
  omnicasa: ["offices", "representatives", "contacts", "search_criteria", "properties", "tasks"],
  zabun: ["offices", "persons", "contacts", "properties", "tasks"],
  zabun360: ["offices", "representatives", "contacts", "estates", "actions"],
};

// Entity types that can be normalized. These are CRM-independent (the same set
// applies to every integration).
export const normalizableEntityTypes = [
  "office",
  "contact",
  "listing",
  "representative",
  "search_criteria",
  "task",
  "relationship",
];

export const companyServices = [
  { id: "popup", name: "Popup", hasPipeline: false, autoActivateCanOverride: false, autoActivateDefault: false },
  {
    id: "automation",
    name: "Automation",
    hasPipeline: false,
    autoActivateCanOverride: false,
    autoActivateDefault: false,
  },
  {
    id: "dashboard",
    name: "Dashboard",
    hasPipeline: false,
    autoActivateCanOverride: false,
  },
  { id: "tracker", name: "Tracker", hasPipeline: false, autoActivateCanOverride: false, autoActivateDefault: false },
];

export const languages = [
  { id: "nl", name: "Nederlands" },
  { id: "fr", name: "Frans" },
  { id: "en", name: "Engels" },
];

export const communicationStyles = [
  { id: "informal", name: "Informal (e.g. Hi ....)" },
  { id: "formal", name: "Formal (more polite, Dear Mr. ...)" },
];

export const emailSafeFonts = [
  { value: "Arial, sans-serif", label: "Arial", description: "The classic — always a safe choice" },
  { value: "Helvetica, Arial, sans-serif", label: "Helvetica", description: "Clean and modern, falls back to Arial on Windows" },
  { value: "Verdana, sans-serif", label: "Verdana", description: "Very readable, slightly wider letterforms" },
  { value: "Tahoma, sans-serif", label: "Tahoma", description: "Crisp and compact, great for Outlook" },
  { value: "Trebuchet MS, sans-serif", label: "Trebuchet MS", description: "A bit more modern, still email-safe" },
  { value: "Georgia, serif", label: "Georgia", description: "Surprisingly readable in emails" },
  { value: "Times New Roman, serif", label: "Times New Roman", description: "Traditional, suited for formal communications" },
];
